import type { EstadoDS } from "@/components/ds";
import { diasHastaVencimiento, estadoVencimiento, type EstadoVencimiento } from "@/lib/vencimiento";
import type { DocumentoTipo } from "@/types/documento-tipo";
import type { PersonaDocumento } from "@/types/persona-documento";

/**
 * Pliegue de la Documentación General de UN trabajador.
 *
 * Vive acá y no dentro del panel porque lo consumen DOS lugares de la misma
 * pantalla: la tarjeta de KPI del encabezado de la Ficha y la tarjeta de
 * progreso del tab. Regla #1 del spec — "un dato, un número": si el
 * encabezado plegara por su cuenta y el tab por la suya, tarde o temprano uno
 * diría 22/24 y el otro 24/24 sobre los mismos documentos.
 *
 * Mismo rol que `documentos-empresa-resumen.ts` para el catálogo de empresa;
 * son catálogos distintos (`documento_tipos` vs `empresa_documento_tipos`) y
 * NO comparten el concepto de "No aplica": los documentos de trabajador no
 * tienen esa marca.
 */

/** Una fila del tab: el tipo del catálogo con el documento que ocupa su slot
 * (o `null`), ya resuelto el semáforo de vigencia. */
export interface SlotDocumentacion {
  tipo: DocumentoTipo;
  documento: PersonaDocumento | null;
  /** `sin_vencimiento` también cuando no hay documento — la fila no vence. */
  vigencia: EstadoVencimiento;
  estado: EstadoDS;
}

/**
 * Estado canónico del DS para un slot — deriva de la presencia de archivo y
 * de la vigencia, EN ESE ORDEN.
 *
 * Un documento cargado y vigente es `subido` (tono verde). El tab lo rotula
 * "Vigente" en vez de "Subido": lo que le importa al prevencionista frente a
 * una fiscalización no es que alguien haya subido el archivo, sino que siga
 * valiendo. El tono es el mismo, así que no se inventa un estado nuevo.
 */
export function estadoSlotDs(documento: PersonaDocumento | null, vigencia: EstadoVencimiento): EstadoDS {
  if (!documento) return "pendiente";
  if (vigencia === "vencido") return "vencido";
  if (vigencia === "por_vencer") return "por_vencer";
  return "subido";
}

/**
 * Cruza el catálogo con los documentos cargados. A lo más un documento por
 * tipo (el backend reemplaza, no acumula).
 *
 * `diasPorVencer` viene de `meta.dias_por_vencer` de `GET /personas/resumen`,
 * NO del default de `lib/vencimiento.ts` — ver la nota de `DIAS_POR_VENCER`.
 * Es el parámetro que mantiene alineados el badge de la fila y el contador
 * del encabezado.
 */
export function construirSlots(
  tipos: DocumentoTipo[],
  documentos: PersonaDocumento[],
  diasPorVencer?: number,
): SlotDocumentacion[] {
  const porTipo = new Map<number, PersonaDocumento>();
  for (const doc of documentos) porTipo.set(doc.tipo_documento_id, doc);

  return tipos.map((tipo) => {
    const documento = porTipo.get(tipo.id) ?? null;
    const vigencia = documento
      ? estadoVencimiento(documento.fecha_vencimiento, undefined, diasPorVencer)
      : ("sin_vencimiento" as const);
    return { tipo, documento, vigencia, estado: estadoSlotDs(documento, vigencia) };
  });
}

/**
 * Urgencia primero, orden del catálogo después.
 *
 * El catálogo llega ordenado grupo→orden desde el backend y ese orden es útil
 * (agrupa lo contractual, lo de salud, lo de licencias). Pero con 20+
 * documentos cargados, un vencido en la posición 34 no se ve. `sort` es
 * estable en JS, así que ordenar sólo por urgencia conserva el orden del
 * catálogo dentro de cada grupo.
 */
const PRIORIDAD: Record<EstadoVencimiento, number> = {
  vencido: 0,
  por_vencer: 1,
  vigente: 2,
  sin_vencimiento: 2,
};

export function ordenarPorUrgencia(slots: SlotDocumentacion[]): SlotDocumentacion[] {
  return [...slots].sort((a, b) => PRIORIDAD[a.vigencia] - PRIORIDAD[b.vigencia]);
}

export interface ResumenDocumentacionGeneral {
  /** Tamaño del catálogo de tipos. Es una REFERENCIA: cuáles corresponden lo
   * declara la empresa en `exigibles`. NUNCA es denominador de cumplimiento. */
  catalogo: number;
  /** Tipos que la empresa declaró exigir. DENOMINADOR de cumplimiento. */
  exigibles: number;
  /** `false` = la empresa todavía no configuró. Ver el tipo del adaptador. */
  exigiblesConfigurados: boolean;
  /** De los exigibles, cuántos tienen documento. NUMERADOR de cumplimiento. */
  exigiblesCargados: number;
  /** Exigibles sin documento. Es la deuda real, la que sí se puede reclamar. */
  exigiblesSinCargar: number;
  /** Tipos con documento cargado, exigibles o no. */
  cargados: number;
  /** Tipos del catálogo sin documento, exigibles o no. */
  sinCargar: number;
  /** Cargados que NO están vencidos. */
  vigentes: number;
  porVencer: number;
  vencidos: number;
}

/**
 * DENOMINADOR = `exigibles`, nunca `catalogo`.
 *
 * El catálogo entero no sirve como denominador: es una lista de referencia y
 * medir contra los 48 convertiría un checklist opcional en una nota roja
 * permanente para todo el mundo. Los `exigibles` son la declaración explícita
 * de la empresa —"esto sí lo pido"— y por eso sí se pueden reclamar.
 *
 * Mientras la empresa no configure nada (`exigiblesConfigurados === false`) no
 * hay denominador legítimo: la pantalla no muestra porcentaje, muestra el
 * catálogo como referencia y ofrece configurar.
 *
 * Los contadores de vigencia (`porVencer`, `vencidos`) van sobre TODO lo
 * cargado, exigible o no: un documento subido y vencido es un hecho aunque la
 * empresa no lo pida. Mismo criterio que `ResumenTrabajadoresService`.
 */
export function resumirDocumentacionGeneral(
  slots: SlotDocumentacion[],
  exigiblesConfigurados = false,
): ResumenDocumentacionGeneral {
  const cargados = slots.filter((s) => s.documento !== null);
  const cuenta = (vigencia: EstadoVencimiento) =>
    cargados.filter((s) => s.vigencia === vigencia).length;
  const vencidos = cuenta("vencido");

  const exigibles = slots.filter((s) => s.tipo.exigido);
  const exigiblesCargados = exigibles.filter((s) => s.documento !== null).length;

  return {
    catalogo: slots.length,
    exigibles: exigibles.length,
    exigiblesConfigurados,
    exigiblesCargados,
    exigiblesSinCargar: exigibles.length - exigiblesCargados,
    cargados: cargados.length,
    sinCargar: slots.length - cargados.length,
    vigentes: cargados.length - vencidos,
    porVencer: cuenta("por_vencer"),
    vencidos,
  };
}

/** Días que faltan (o que pasaron, en negativo) para el vencimiento de un
 * slot. `null` cuando el documento no vence o no existe. */
export function diasDeSlot(slot: SlotDocumentacion): number | null {
  if (!slot.documento?.fecha_vencimiento) return null;
  return diasHastaVencimiento(slot.documento.fecha_vencimiento);
}
